import { useEffect, useMemo, useRef, useState, type DragEvent } from 'react';
import { useNavigate } from 'react-router';
import { useQueryClient } from '@tanstack/react-query';
import { Check, Pause, Play, RefreshCw, Upload, X } from 'lucide-react';
import { useT } from '@/i18n';
import { ErrorBox, Ltr, QueryView, Spinner } from '@/components/ui';
import { api } from '@/lib/api/endpoints';
import { qk } from '@/lib/api/keys';
import { useWs } from '@/lib/api/hooks';
import { eta, formatBytes } from '@/lib/format';
import { listRecords, partCount } from '@/lib/upload/plan';
import { logError, logInfo } from '@/lib/log';
import { hasActiveUploads, parsePeople, useUploads, type UploadItem } from '@/store/uploads';
import type { SessionSource, SourceKind } from '@/lib/api/types';
import { SOURCE_KINDS } from '@/lib/constants';
import { EpisodeHeader, useEpisodeContext } from './context';
import { LocalFiles } from './LocalFiles';
import { MultitrackPanel, multitrackUndecided } from './MultitrackPanel';

function UploadRow({ item }: { item: UploadItem }) {
  const { t, td } = useT();
  const { update, pause, resume, remove } = useUploads();
  const pct = item.total ? Math.round((item.bytes / item.total) * 100) : 0;
  const editable = item.status === 'ready' || item.status === 'failed';
  const statusText =
    item.status === 'done' ? t('uploadDone')
    : item.status === 'failed' ? `${t('uploadFailed')}: ${item.error ?? ''}`
    : item.status === 'paused' ? t('uploadPause')
    : item.status === 'queued' ? t('uploadWaiting')
    : item.status === 'uploading' ? t('uploadSpeed', { speed: formatBytes(item.speed), eta: item.speed > 0 ? eta((item.total - item.bytes) / item.speed) : '—' })
    : formatBytes(item.total);
  return (
    <div className="card-row" style={{ display: 'grid', gap: 8, padding: '12px 16px' }}>
      <div className="row wrap" style={{ gap: 10 }}>
        <Ltr className="mono ellipsis" ><span style={{ fontSize: 12, fontWeight: 500 }}>{item.file.name}</span></Ltr>
        <span className="muted small">{formatBytes(item.total)}</span>
        <div className="row push" style={{ gap: 6 }}>
          {item.status === 'uploading' || item.status === 'queued' ? (
            <button type="button" className="btn btn-sm" onClick={() => pause(item.id)}><Pause size={13} aria-hidden />{t('uploadPause')}</button>
          ) : null}
          {item.status === 'paused' ? (
            <button type="button" className="btn btn-sm" onClick={() => resume(item.id)}><Play size={13} aria-hidden />{t('uploadResume')}</button>
          ) : null}
          {item.status === 'failed' ? (
            <button type="button" className="btn btn-sm" onClick={() => resume(item.id)}><RefreshCw size={13} aria-hidden />{t('uploadRetry')}</button>
          ) : null}
          {item.status === 'done' ? <span className="pill" style={{ background: 'var(--green-soft)', color: 'var(--green)' }}><Check size={12} aria-hidden />{t('uploadDone')}</span> : (
            <button type="button" className="btn btn-ghost btn-icon" style={{ width: 28, height: 28 }} aria-label={`${t('remove')} ${item.file.name}`} onClick={() => remove(item.id)}><X size={14} aria-hidden /></button>
          )}
        </div>
      </div>
      <div style={{ display: 'grid', gridTemplateColumns: 'minmax(110px, 140px) minmax(0,1fr) minmax(0,1fr)', gap: 8 }}>
        <select className="select" aria-label={`${t('fileKind')} · ${item.file.name}`} disabled={!editable} value={item.kind} onChange={(e) => update(item.id, { kind: e.target.value as SourceKind })} style={{ height: 32 }}>
          {SOURCE_KINDS.map((k) => <option key={k} value={k}>{td(`kind_${k}`)}</option>)}
        </select>
        <input className="input" style={{ height: 32 }} aria-label={`${t('filePerson')} · ${item.file.name}`} placeholder={t('filePersonPh')} disabled={!editable} value={item.person} onChange={(e) => update(item.id, { person: e.target.value })} />
        <input className="input" style={{ height: 32 }} aria-label={`${t('fileLabel')} · ${item.file.name}`} placeholder={t('fileLabel')} disabled={!editable} value={item.label} onChange={(e) => update(item.id, { label: e.target.value })} />
      </div>
      {item.kind === 'camera' ? (
        <label className="row wrap" style={{ gap: 8, fontSize: 12 }}>
          <span className="muted">{t('filePeople')}</span>
          <input className="input" style={{ height: 32, flex: '1 1 220px' }} aria-label={`${t('filePeople')} · ${item.file.name}`} placeholder={t('filePeoplePh')} disabled={!editable} value={item.people} onChange={(e) => update(item.id, { people: e.target.value })} />
          <span className="muted small">{t('filePeopleHint')}</span>
        </label>
      ) : null}
      {item.status !== 'ready' ? (
        <div className="stack" style={{ gap: 4 }}>
          <div className="progress" role="progressbar" aria-label={item.file.name} aria-valuemin={0} aria-valuemax={100} aria-valuenow={pct} data-state={item.status === 'done' ? 'done' : item.status === 'failed' ? 'failed' : undefined}>
            <span style={{ width: `${pct}%` }} />
          </div>
          <span className="small" style={{ color: item.status === 'failed' ? 'var(--accent-text)' : 'var(--muted)' }} aria-live="polite">
            <Ltr className="mono">{pct}%</Ltr> · {statusText}
            {item.resumedFrom && item.resumedFrom > 1 ? ` · ${t('uploadResumed', { n: item.resumedFrom })}` : ''}
          </span>
        </div>
      ) : null}
    </div>
  );
}

/** Retag who is in an uploaded camera's frame; the planner uses it for two- and three-shots on the next run. */
function SourcePeople({ sessionId, source }: { sessionId: string; source: SessionSource }) {
  const { t } = useT();
  const qc = useQueryClient();
  const w = useWs();
  const saved = (source.people ?? []).join(', ');
  const [text, setText] = useState(saved);
  const [state, setState] = useState<'idle' | 'saving' | 'saved'>('idle');
  const [error, setError] = useState<unknown>(null);
  useEffect(() => setText(saved), [saved]);
  const save = async () => {
    const people = parsePeople(text);
    setState('saving');
    setError(null);
    try {
      await api.pipeline.setPeople(sessionId, source.id, people);
      logInfo('upload', `people tagged source=${source.id} n=${people.length}`);
      await qc.invalidateQueries({ queryKey: qk.session(w, sessionId) });
      setState('saved');
    } catch (e) {
      logError('upload', `could not tag people on source=${source.id}`, e);
      setError(e);
      setState('idle');
    }
  };
  return (
    <div className="row wrap" style={{ gap: 6, flex: '1 1 260px' }}>
      <input className="input" style={{ height: 28, flex: 1, minWidth: 140 }} aria-label={`${t('filePeople')} · ${source.filename}`} placeholder={t('filePeoplePh')} value={text} onChange={(e) => { setText(e.target.value); setState('idle'); }} />
      <button type="button" className="btn btn-xs" disabled={state === 'saving' || text === saved} onClick={save}>{state === 'saved' ? t('saved') : t('save')}</button>
      {error ? <ErrorBox error={error} /> : null}
    </div>
  );
}

export function UploadPage() {
  const { t, td } = useT();
  const navigate = useNavigate();
  const qc = useQueryClient();
  const w = useWs();
  const ctx = useEpisodeContext();
  const items = useUploads((s) => s.items);
  const add = useUploads((s) => s.add);
  const startAll = useUploads((s) => s.startAll);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [starting, setStarting] = useState(false);
  const [dragOver, setDragOver] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const sessionId = ctx.sessionId;
  const mine = items.filter((i) => i.sessionId === sessionId);
  const active = mine.some((i) => i.status === 'uploading' || i.status === 'queued');
  const ready = mine.filter((i) => i.status === 'ready' || i.status === 'failed');
  const session = ctx.session.data;
  const uploadedSources = session?.sources.filter((s) => s.uploaded) ?? [];
  const doneHere = mine.filter((i) => i.status === 'done').length;
  // Amendment 24: every multitrack mic must be split or kept as a mixdown first.
  const undecided = multitrackUndecided(uploadedSources);
  const canProcess = !active && !undecided && (uploadedSources.length > 0 || doneHere > 0) && (!session || session.status === 'uploading' || session.status === 'failed');
  const splittable = !!session && !session.run && session.status === 'uploading';

  const resumables = useMemo(
    () => (sessionId ? listRecords(sessionId).filter((r) => !mine.some((i) => i.file.name === r.filename && i.file.size === r.size)) : []),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [sessionId, mine.length],
  );

  useEffect(() => {
    const warn = (e: BeforeUnloadEvent) => {
      if (hasActiveUploads()) {
        e.preventDefault();
        e.returnValue = t('uploadLeaveWarn');
      }
    };
    window.addEventListener('beforeunload', warn);
    return () => window.removeEventListener('beforeunload', warn);
  }, [t]);

  // Refresh the session (sources list) whenever a file finishes uploading.
  useEffect(() => {
    if (doneHere && sessionId) void qc.invalidateQueries({ queryKey: qk.session(w, sessionId) });
  }, [doneHere, sessionId, qc, w]);

  const ensureSession = async (): Promise<string | null> => {
    if (sessionId) return sessionId;
    setCreating(true);
    try {
      const s = await api.pipeline.createSession(ctx.episodeId);
      logInfo('upload', `created session=${s.id} episode=${ctx.episodeId}`);
      await qc.invalidateQueries({ queryKey: qk.sessions(w, ctx.episodeId) });
      await qc.invalidateQueries({ queryKey: qk.project(w, ctx.projectId) });
      return s.id;
    } catch (e) {
      logError('upload', `could not create a session for episode=${ctx.episodeId}`, e);
      setError(e);
      return null;
    } finally {
      setCreating(false);
    }
  };

  const addFiles = async (list: FileList | File[] | null) => {
    // Copy first: the input's FileList is cleared right after this call returns.
    const files = list ? [...list] : [];
    if (files.length === 0) return;
    setError(null);
    const id = await ensureSession();
    if (id) add(id, files);
  };

  const onDrop = (e: DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    void addFiles(e.dataTransfer.files);
  };

  const process = async () => {
    if (!sessionId) return;
    setStarting(true);
    setError(null);
    try {
      await api.pipeline.process(sessionId, session?.status === 'failed');
      logInfo('pipeline', `processing started session=${sessionId}`);
      await qc.invalidateQueries({ queryKey: qk.session(w, sessionId) });
      useUploads.getState().clearDone(sessionId);
      navigate(`/projects/${ctx.projectId}/episodes/${ctx.episodeId}`);
    } catch (e) {
      logError('pipeline', `could not start processing session=${sessionId}`, e);
      setError(e);
    } finally {
      setStarting(false);
    }
  };

  return (
    <div className="page" style={{ maxWidth: 1100 }}>
      <QueryView query={ctx.project}>
        {(project) =>
          !ctx.episode ? (
            <div className="alert alert-error">{t('episodeNotFound')}</div>
          ) : (
            <>
              <EpisodeHeader
                project={project}
                episode={ctx.episode}
                actions={
                  <button type="button" className="btn btn-primary" disabled={!canProcess || starting} onClick={process} title={canProcess ? undefined : undecided ? t('mtPending') : t('needOneFile')}>
                    {starting ? t('startingProcessing') : t('startProcessing')}
                  </button>
                }
              />
              {error ? <ErrorBox error={error} /> : null}
              <section className="card" aria-labelledby="up-h">
                <div className="card-head" style={{ display: 'grid', gap: 2 }}>
                  <h2 id="up-h" className="h2">{t('uploadTitle')}</h2>
                  <span className="muted small">{t('uploadSub')}</span>
                </div>
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragOver(true);
                  }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={onDrop}
                  style={{ margin: 16, padding: 28, borderRadius: 8, border: `1.5px dashed ${dragOver ? 'var(--blue)' : 'var(--line-2)'}`, background: dragOver ? 'var(--blue-soft)' : 'var(--card-2)', display: 'grid', placeItems: 'center', gap: 8, textAlign: 'center' }}
                >
                  <Upload size={22} aria-hidden style={{ color: 'var(--muted)' }} />
                  <span>
                    {t('dropHere')}{' '}
                    <button type="button" className="btn-link" style={{ fontSize: 14 }} onClick={() => input.current?.click()} disabled={creating}>{t('browse')}</button>
                  </span>
                  {creating ? <Spinner label={t('loading')} /> : null}
                  <span className="muted small">{t('uploadFormatHint')}</span>
                  <input ref={input} type="file" multiple hidden accept="video/*,audio/*,.mxf,.mov,.mp4,.wav,.m4a,.mp3,.flac" onChange={(e) => { void addFiles(e.target.files); e.target.value = ''; }} data-testid="file-input" />
                </div>
                {resumables.map((r) => (
                  <div key={r.fp} className="card-row row" style={{ padding: '10px 16px' }}>
                    <Ltr className="mono ellipsis"><span style={{ fontSize: 12 }}>{r.filename}</span></Ltr>
                    <span className="muted small">{formatBytes(r.size)} · {Object.keys(r.done).length}/{partCount(r.size, r.part_size)}</span>
                    <span className="small push" style={{ color: 'var(--amber)' }}>{t('uploadResumable')}</span>
                  </div>
                ))}
                {mine.map((i) => <UploadRow key={i.id} item={i} />)}
                {ready.length ? (
                  <div className="row" style={{ padding: '12px 16px' }}>
                    <button type="button" className="btn btn-inv push" onClick={() => sessionId && startAll(sessionId)}>{t('uploadAll', { n: ready.length })}</button>
                  </div>
                ) : null}
              </section>
              {undecided ? <div className="alert" role="status" style={{ color: 'var(--amber)' }}>{t('mtPending')}</div> : null}
              <LocalFiles session={session} ensureSession={ensureSession} />
              {sessionId ? <MultitrackPanel sessionId={sessionId} sources={uploadedSources} editable={splittable} /> : null}
              {uploadedSources.length ? (
                <section className="card" aria-labelledby="src-h">
                  <div className="card-head"><h2 id="src-h" className="h2">{t('uploadDone')} · {uploadedSources.length}</h2></div>
                  {uploadedSources.map((s) => (
                    <div key={s.id} className="card-row row" style={{ padding: '10px 16px' }}>
                      <Check size={14} aria-hidden style={{ color: 'var(--green)' }} />
                      <Ltr className="mono ellipsis"><span style={{ fontSize: 12 }}>{s.filename}</span></Ltr>
                      <span className="tag">{td(`kind_${s.kind}`)}</span>
                      {s.local ? <span className="tag" title={session?.routing?.device ? t('localAdded', { name: session.routing.device.name }) : undefined}>{t('localTag')}</span> : null}
                      {s.channel !== null && s.channel !== undefined ? <span className="tag">{t('mtChTag', { n: s.channel + 1 })}</span> : null}
                      {s.person ? <span className="small">{s.person}</span> : null}
                      {s.kind === 'camera' && sessionId ? <SourcePeople sessionId={sessionId} source={s} /> : null}
                      <span className="muted small push">{formatBytes(s.size)}</span>
                    </div>
                  ))}
                </section>
              ) : null}
            </>
          )
        }
      </QueryView>
    </div>
  );
}
